import '~/utils/initialize';
import { ReactNode, useEffect } from 'react';
import Head from 'next/head';
import { NextComponentType } from 'next';
import { AppContext, AppInitialProps, AppLayoutProps } from 'next/app';
import makeInspectable from 'mobx-devtools-mst';
import { onPatch } from 'mobx-state-tree';
import { QueryClientProvider, QueryClient } from 'react-query';
import { ReactQueryDevtools } from 'react-query/devtools';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { SnackbarProvider } from 'notistack';
import { CacheProvider, EmotionCache } from '@emotion/react';
import * as Sentry from '@sentry/nextjs';
import jsCookie from 'js-cookie';
import createEmotionCache from '~/utils/createEmotionCache';
import { initializeStore, StoreProvider } from '~/stores';
import env from '~/env';
import '~/styles/global.scss';
import '~/styles/build.css';
import theme from '~/styles/theme';
import { TOKEN } from '~/src/utils/constant';
import { checkOsMobile } from '~/utils/commonUtils';
import { analytics } from '~/utils/firebase';
import Toast from '~/components/toast';
import {
  QueryClientProvider as QueryClientProviderV5,
  QueryClient as QueryClientV5,
} from '@tanstack/react-query';
import { OverlayProvider } from '@toss/use-overlay';

// Client-side cache, shared for the whole session of the user in the browser.
const clientSideEmotionCache = createEmotionCache();

/** 센트리 설정 */
Sentry.init({
  dsn: env.SENTRY_DSN,

  // 실제 적용시
  // dsn: env.NODE_ENV === 'production' ? 'https://2cb3c48f41d44a2b8a3b929757ef9f7e@o564957.ingest.sentry.io/6048677' : false

  // env 환경
  environment: env.NODE_ENV,

  // Set tracesSampleRate to 1.0 to capture 100%
  // of transactions for performance monitoring.
  // We recommend adjusting this value in production
  tracesSampleRate: 1.0,
});

/** react-query 기본 설정 */
const reactQueryClient = new QueryClient({
  defaultOptions: {
    queries: {
      keepPreviousData: false,
    },
  },
});

interface AppProps extends AppLayoutProps {
  emotionCache?: EmotionCache;
}

const App: NextComponentType<AppContext, AppInitialProps, AppLayoutProps> = (
  props: AppProps,
) => {
  const queryClient = new QueryClient();

  const { Component, pageProps, emotionCache = clientSideEmotionCache } = props;
  const getLayout = Component.getLayout || ((page: ReactNode) => page);
  const store = initializeStore(pageProps.initialState);
  const token = jsCookie.get(TOKEN);

  useEffect(() => {
    analytics;
  }, []);

  // 토큰 있다면 store에 로그인 사용자 정보 set
  if (token) {
    store.authModel.setLoginInfo();
  }

  // mst 디버깅 로그
  if (env.NODE_ENV === 'development' && env.IS_DEBUG) {
    // 크롬 console 에 해당값의 변화가 있을 때 나타나게 함
    onPatch(store, (patch) => {
      console.info(patch);
    });

    // 크롬 mobx tools 에 MST 로 상태변화를 볼 수 있게 한다.
    makeInspectable(store);
  }

  // ios 에서 input 박스에 포커싱이 갔을 때 키보드 영역 때문에 fix영역 하위에 빈영역이 생겨 아래와 같이 대응
  const handleTouchMove = () => {
    const { os } = checkOsMobile();

    if (os === 'ios') {
      Array.from(document.querySelectorAll<HTMLElement>('input,textarea')).map(
        (ele: HTMLElement) => ele.blur(),
      );
    }
  };

  return (
    <StoreProvider value={store}>
      <CacheProvider value={emotionCache}>
        <Head>
          <title>Getcha Admin</title>
          <meta
            name='viewport'
            content='width=device-width, initial-scale=1.0, user-scalable=no, maximum-scale=1.0, minimum-scale=1.0'
          />
          <meta name='color-scheme' content='light only' />
          <meta http-equiv='Expires' content='720' />
        </Head>
        <ThemeProvider theme={theme}>
          <OverlayProvider>
            <Toast ref={(ref) => Toast.setRef(ref as any)} />

            <CssBaseline />
            <QueryClientProviderV5 client={queryClient}>
              <QueryClientProvider client={reactQueryClient}>
                <ReactQueryDevtools
                  initialIsOpen={false}
                  position='bottom-right'
                />
                <SnackbarProvider maxSnack={3}>
                  <div className='h-full' onTouchMove={handleTouchMove}>
                    {getLayout(<Component {...pageProps} />)}
                  </div>
                </SnackbarProvider>
              </QueryClientProvider>
            </QueryClientProviderV5>
          </OverlayProvider>
        </ThemeProvider>
      </CacheProvider>
    </StoreProvider>
  );
};

export default App;
